import { useEffect, useRef, useState, useCallback, type RefObject } from 'react'

// Shared IntersectionObserver singleton — one observer for all Animate components
// instead of creating one per component instance (10-30+ per page → 1)
const observers = new Map<string, IntersectionObserver>()
const callbacks = new WeakMap<Element, (isIntersecting: boolean) => void>()

function getSharedObserver(
  optionsKey: string,
  options: IntersectionObserverInit,
): IntersectionObserver {
  let observer = observers.get(optionsKey)
  if (!observer) {
    observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const cb = callbacks.get(entry.target)
        if (cb && entry.isIntersecting) {
          cb(true)
          observer!.unobserve(entry.target)
          callbacks.delete(entry.target)
        }
      }
    }, options)
    observers.set(optionsKey, observer)
  }
  return observer
}

export function useInView<T extends HTMLElement = HTMLDivElement>(
  options?: IntersectionObserverInit,
): [RefObject<T>, boolean] {
  const ref = useRef<T>(null!)
  const [inView, setInView] = useState(false)

  const threshold = options?.threshold ?? 0.1
  const rootMargin = options?.rootMargin ?? '-80px'
  // Stable key for observer reuse
  const optionsKey = `${threshold}_${rootMargin}`

  useEffect(() => {
    const el = ref.current
    if (!el || inView) return

    const observer = getSharedObserver(optionsKey, {
      threshold: threshold as number,
      rootMargin,
      ...options,
    })

    callbacks.set(el, () => setInView(true))
    observer.observe(el)

    return () => {
      observer.unobserve(el)
      callbacks.delete(el)
    }
  }, [inView])

  return [ref, inView]
}
